import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { ClipboardList, CheckCircle2, Archive as ArchiveIcon, RotateCcw, Printer } from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { id: 'open', label: 'Open' },
  { id: 'completed', label: 'Completed' },
  { id: 'archived', label: 'Archived' },
  { id: 'abandoned', label: 'Abandoned' },
  { id: 'by-state', label: 'Sales by State', noCount: true },
];

function AdminOrders({ onLogout }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('open');
  const [updatingId, setUpdatingId] = useState(null);
  const [abandoned, setAbandoned] = useState([]);
  const [backfilling, setBackfilling] = useState('');

  useEffect(() => {
    fetchOrders();
    fetchAbandoned();
  }, []);

  const fetchAbandoned = async () => {
    try {
      const response = await axios.get('/admin/orders/abandoned');
      setAbandoned(response.data);
    } catch (error) {
      console.error('Error fetching abandoned checkouts:', error);
    }
  };

  // Older orders were saved before shipping and tax were recorded on them;
  // this looks each one up in Stripe, a batch at a time.
  const backfillCharges = async () => {
    setBackfilling('Starting…');
    try {
      let updated = 0;
      for (;;) {
        const { data } = await axios.post('/admin/orders/backfill-charges');
        updated += data.updated;
        if (data.remaining === 0 || data.updated + data.failed === 0) break;
        setBackfilling(`${updated} filled in, ${data.remaining} to go…`);
      }
      toast.success(`Filled in shipping and tax on ${updated} past order(s)`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to look up past orders in Stripe');
    } finally {
      setBackfilling('');
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get('/admin/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const counts = useMemo(() => {
    const byStatus = orders.reduce((acc, o) => {
      acc[o.status] = (acc[o.status] || 0) + 1;
      return acc;
    }, {});
    byStatus.abandoned = abandoned.length;
    return byStatus;
  }, [orders, abandoned]);

  // Summary for the Abandoned tab. "In progress" checkouts (Stripe session
  // under 24h old) might still get paid, so they're kept out of the lost total.
  const abandonedStats = useMemo(() => {
    const lost = abandoned.filter((a) => !a.in_progress && !a.completed_as);
    return {
      lostCount: lost.length,
      lostValue: lost.reduce((sum, a) => sum + (a.subtotal || 0), 0),
      retriedCount: abandoned.filter((a) => a.completed_as).length,
    };
  }, [abandoned]);

  // Revenue totals across every shop, independent of the open/completed/
  // archived tab filter — every order here is already payment_status "paid"
  // (see /admin/orders), so this is real sales, not just fulfillment status.
  const salesTotals = useMemo(() => {
    const now = new Date();
    const totals = {
      today: { total: 0, count: 0 },
      month: { total: 0, count: 0 },
      year: { total: 0, count: 0 },
    };
    orders.forEach((o) => {
      const date = new Date(o.created_at);
      if (Number.isNaN(date.getTime())) return;
      const amount = o.total || 0;
      if (date.getFullYear() === now.getFullYear()) {
        totals.year.total += amount;
        totals.year.count += 1;
        if (date.getMonth() === now.getMonth()) {
          totals.month.total += amount;
          totals.month.count += 1;
          if (date.toDateString() === now.toDateString()) {
            totals.today.total += amount;
            totals.today.count += 1;
          }
        }
      }
    });
    return totals;
  }, [orders]);

  const visibleOrders = useMemo(
    () => orders.filter((o) => o.status === activeTab),
    [orders, activeTab]
  );

  const setStatus = async (order, status) => {
    setUpdatingId(order.id);
    try {
      await axios.patch(`/admin/orders/${order.shop}/${order.id}`, { status });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status } : o)));
      toast.success('Order updated');
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
  };

  return (
    <AdminLayout onLogout={onLogout} currentPage="orders">
      <div className="page-header">
        <h1>Orders</h1>
        {orders.some((o) => !o.charges_recorded) && (
          <button className="btn btn-secondary" onClick={backfillCharges} disabled={!!backfilling}
            title="Look up shipping and sales tax for older orders in Stripe">
            {backfilling || `Fill in shipping & tax for ${orders.filter((o) => !o.charges_recorded).length} past orders`}
          </button>
        )}
      </div>

      {!loading && (
        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {[
            { label: 'Today', data: salesTotals.today },
            { label: 'This Month', data: salesTotals.month },
            { label: 'This Year', data: salesTotals.year },
          ].map(({ label, data }) => (
            <div key={label} style={{
              flex: '1 1 180px',
              background: '#fff',
              border: '1px solid #eee',
              borderRadius: '8px',
              padding: '14px 18px'
            }}>
              <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
              <div style={{ fontSize: '24px', fontWeight: '700', marginTop: '4px' }}>${data.total.toFixed(2)}</div>
              <div style={{ fontSize: '13px', color: '#666' }}>{data.count} order{data.count === 1 ? '' : 's'}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={activeTab === tab.id ? 'btn btn-primary' : 'btn btn-secondary'}
          >
            {tab.label}{!tab.noCount && ` (${counts[tab.id] || 0})`}
          </button>
        ))}
      </div>

      {activeTab === 'by-state' ? (
        <SalesByState />
      ) : activeTab === 'abandoned' ? (
        <AbandonedTable abandoned={abandoned} stats={abandonedStats} formatDate={formatDate} />
      ) : loading ? (
        <div className="loading-screen">
          <div className="loading-spinner"></div>
        </div>
      ) : visibleOrders.length === 0 ? (
        <div className="empty-state">
          <ClipboardList size={48} strokeWidth={1} />
          <h3>No {activeTab} orders</h3>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Order #</th>
                <th></th>
                <th>Shop</th>
                <th>Item(s)</th>
                <th>Customer</th>
                <th>Shipping</th>
                <th>Sales Tax</th>
                <th>Total</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleOrders.map((order) => (
                <tr key={`${order.shop}-${order.id}`}>
                  <td>{order.order_number}</td>
                  <td>
                    {order.image_url && (
                      <a href={order.image_url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={order.image_url}
                          alt=""
                          style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '4px', display: 'block' }}
                        />
                      </a>
                    )}
                  </td>
                  <td>{order.shop_label}</td>
                  <td>
                    {order.summary}
                    {order.order_notes && (
                      <div
                        title={order.order_notes}
                        style={{
                          marginTop: '6px', padding: '6px 8px', background: '#fef3c7', borderRadius: '4px',
                          fontSize: '12px', color: '#92400e', maxWidth: '280px', whiteSpace: 'pre-wrap'
                        }}
                      >
                        <strong>Note:</strong> {order.order_notes}
                      </div>
                    )}
                  </td>
                  <td>
                    <div>{order.customer_name || '-'}</div>
                    {order.customer_email && (
                      <div style={{ fontSize: '12px', color: '#888' }}>{order.customer_email}</div>
                    )}
                  </td>
                  <td>{order.shipping == null ? '—' : `$${order.shipping.toFixed(2)}`}</td>
                  <td>{order.tax == null ? '—' : `$${order.tax.toFixed(2)}`}</td>
                  <td>${order.total?.toFixed(2)}</td>
                  <td>{formatDate(order.created_at)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Link
                        className="btn btn-secondary"
                        to={`/admin/orders/${order.shop}/${order.id}/slip`}
                        title="Packing Slip"
                      >
                        <Printer size={16} /> Slip
                      </Link>
                      {order.status === 'open' && (
                        <button
                          className="btn btn-secondary"
                          disabled={updatingId === order.id}
                          onClick={() => setStatus(order, 'completed')}
                          title="Mark Completed"
                        >
                          <CheckCircle2 size={16} /> Complete
                        </button>
                      )}
                      {order.status === 'completed' && (
                        <>
                          <button
                            className="btn btn-secondary"
                            disabled={updatingId === order.id}
                            onClick={() => setStatus(order, 'open')}
                            title="Reopen"
                          >
                            <RotateCcw size={16} /> Reopen
                          </button>
                          <button
                            className="btn btn-secondary"
                            disabled={updatingId === order.id}
                            onClick={() => setStatus(order, 'archived')}
                            title="Archive"
                          >
                            <ArchiveIcon size={16} /> Archive
                          </button>
                        </>
                      )}
                      {order.status === 'archived' && (
                        <button
                          className="btn btn-secondary"
                          disabled={updatingId === order.id}
                          onClick={() => setStatus(order, 'completed')}
                          title="Restore"
                        >
                          <RotateCcw size={16} /> Restore
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

// States with a rule Roy is tracking: NC is home (registered), VA is next door
// and has a 200-orders-a-year threshold for out-of-state sellers.
function currentMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const money = (n) => (n == null ? '—' : `$${n.toFixed(2)}`);

function csvCell(value) {
  const text = value == null ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

// One state's paid orders for one month, listed individually.
function StateMonthOrders({ stateOptions }) {
  const [state, setState] = useState('NC');
  const [month, setMonth] = useState(currentMonthISO());
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (!month) return;
    let cancelled = false;
    setOrders(null);
    axios.get('/admin/orders/state-month', { params: { state, month } })
      .then((resp) => { if (!cancelled) setOrders(resp.data); })
      .catch(() => { if (!cancelled) { setOrders([]); toast.error('Failed to load orders'); } });
    return () => { cancelled = true; };
  }, [state, month]);

  const sum = (key) => (orders || []).reduce((acc, o) => acc + (o[key] || 0), 0);

  const downloadCsv = () => {
    const header = ['Order #', 'Date', 'Customer', 'Email', 'City', 'ZIP', 'Item(s)', 'Shipping', 'Sales Tax', 'Total'];
    const rows = orders.map((o) => [
      o.order_number, new Date(o.created_at).toLocaleDateString(), o.customer_name, o.customer_email,
      o.city, o.postal_code, o.summary,
      o.shipping == null ? '' : o.shipping.toFixed(2), o.tax == null ? '' : o.tax.toFixed(2), o.total.toFixed(2),
    ]);
    const csv = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\n') + '\n';
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders-${state}-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ marginTop: '32px' }}>
      <h3 style={{ margin: '0 0 12px' }}>Orders by state and month</h3>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
        <select className="form-input" style={{ width: 'auto' }} value={state} onChange={(e) => setState(e.target.value)}>
          {stateOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="month" className="form-input" style={{ width: 'auto' }} value={month} onChange={(e) => setMonth(e.target.value)} />
        {orders && orders.length > 0 && (
          <button className="btn btn-secondary" onClick={downloadCsv}>Download CSV</button>
        )}
      </div>
      {!orders ? (
        <div className="loading-spinner"></div>
      ) : orders.length === 0 ? (
        <p style={{ color: '#666' }}>No paid orders shipped to {state} in this month.</p>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Ship to</th>
                <th>Item(s)</th>
                <th>Shipping</th>
                <th>Sales Tax</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={`${o.shop}-${o.id}`}>
                  <td>{o.order_number}</td>
                  <td>{new Date(o.created_at).toLocaleDateString()}</td>
                  <td>
                    <div>{o.customer_name || '-'}</div>
                    {o.customer_email && <div style={{ fontSize: '12px', color: '#888' }}>{o.customer_email}</div>}
                  </td>
                  <td>{[o.city, o.postal_code].filter(Boolean).join(' ') || '—'}</td>
                  <td>{o.summary}</td>
                  <td>{money(o.shipping)}</td>
                  <td>{money(o.tax)}</td>
                  <td>{money(o.total)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid #ddd', fontWeight: '700' }}>
                <td colSpan={5}>{orders.length} order{orders.length === 1 ? '' : 's'}</td>
                <td>{money(sum('shipping'))}</td>
                <td>{money(sum('tax'))}</td>
                <td>{money(sum('total'))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const FLAG_AT = 0.75; // flag a state once it reaches this share of either threshold

function dollars(value) {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatStamp(iso) {
  if (!iso) return 'never';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function isRealState(code) {
  return /^[A-Z]{2}$/.test(code);
}

// How close one year's numbers are to a state's threshold: the larger of the
// order-count share and the dollar share (a state is over if it passes either).
function thresholdShare(period, rule) {
  const shares = [];
  if (rule.orders) shares.push(period.orders / rule.orders);
  if (rule.sales) shares.push((rule.basis === 'gross' ? period.gross : period.goods) / rule.sales);
  return shares.length ? Math.max(...shares) : 0;
}

function ruleFor(state, config) {
  return config.overrides[state] || {
    orders: config.default_orders, sales: config.default_sales, basis: config.default_basis,
  };
}

function stateStatus(row, config) {
  if (!isRealState(row.state)) return { rank: -2, label: '—' };
  if (config.registered.includes(row.state)) return { rank: -1, label: 'Registered', color: '#166534' };
  const rule = ruleFor(row.state, config);
  const share = Math.max(thresholdShare(row.current, rule), thresholdShare(row.previous, rule));
  const pct = Math.round(share * 100);
  if (share >= 1) return { rank: share, label: `Over (${pct}%)`, color: '#b91c1c', background: '#fee2e2', bold: true };
  if (share >= FLAG_AT) return { rank: share, label: `Near (${pct}%)`, color: '#b45309', background: '#fef3c7', bold: true };
  return { rank: share, label: `${pct}%`, color: '#888' };
}

const SORTS = {
  state: (row) => row.state,
  prevOrders: (row) => row.previous.orders,
  prevSales: (row) => row.previous.goods,
  curOrders: (row) => row.current.orders,
  curSales: (row) => row.current.goods,
  status: (row) => row.status.rank,
};

function SalesByState() {
  const [report, setReport] = useState(null);
  const [config, setConfig] = useState(null);
  const [refundNote, setRefundNote] = useState('Checking Stripe for refunds…');
  const [sort, setSort] = useState({ key: 'status', desc: true });

  const loadReport = async () => {
    const [reportResp, configResp] = await Promise.all([
      axios.get('/admin/orders/sales-by-state'),
      axios.get('/admin/sales-tax/thresholds'),
    ]);
    setReport(reportResp.data);
    setConfig(configResp.data);
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        await loadReport();
      } catch (error) {
        toast.error('Failed to load sales by state');
        return;
      }
      // Then record any new Stripe refunds (the first time, every past refund) and reload.
      try {
        let updated = 0;
        for (let i = 0; i < 20; i += 1) {
          const { data } = await axios.post('/admin/orders/sync-refunds');
          updated += data.orders_updated;
          if (cancelled) return;
          setRefundNote(data.remaining ? `Checking Stripe for refunds… ${data.remaining} to go` : '');
          if (!data.remaining) break;
        }
        if (updated && !cancelled) await loadReport();
      } catch (error) {
        if (!cancelled) setRefundNote(`Couldn't check Stripe for refunds: ${error.response?.data?.detail || 'error'}`);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  if (!report || !config) {
    return <div className="loading-screen"><div className="loading-spinner"></div></div>;
  }

  const { current_year: curYear, previous_year: prevYear } = report;
  const rows = report.states.map((row) => ({ ...row, status: stateStatus(row, config) }));
  const sortValue = SORTS[sort.key];
  rows.sort((a, b) => {
    const x = sortValue(a);
    const y = sortValue(b);
    const order = x < y ? -1 : x > y ? 1 : a.state.localeCompare(b.state);
    return sort.desc ? -order : order;
  });
  const flagged = rows.filter((r) => r.status.rank >= FLAG_AT);
  const firstOrder = report.first_order_at ? new Date(report.first_order_at) : null;
  const prevYearPartial = firstOrder && firstOrder.getFullYear() >= prevYear;

  const header = (key, label) => (
    <th onClick={() => setSort({ key, desc: sort.key === key ? !sort.desc : key !== 'state' })}
      style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}>
      {label}{sort.key === key ? (sort.desc ? ' ▼' : ' ▲') : ''}
    </th>
  );
  const salesCell = (period) => (
    <td>
      {dollars(period.goods)}
      {period.gross !== period.goods && <div style={{ fontSize: '12px', color: '#999' }}>gross {dollars(period.gross)}</div>}
    </td>
  );
  const totalCell = (period) => (
    <td style={{ fontWeight: '700' }}>
      {dollars(period.goods)}
      <div style={{ fontSize: '12px', color: '#999', fontWeight: '400' }}>gross {dollars(period.gross)}</div>
    </td>
  );

  return (
    <>
      <p style={{ fontSize: '14px', color: '#555', margin: '0 0 12px', lineHeight: 1.6 }}>
        Paid shop orders by ship-to state, this year and last. Only real shop orders count. Beach Bum Society
        memberships and donations are left out (Stripe's own threshold count includes them), and orders refunded in
        full are left out too. <strong>Sales</strong> is goods only (no shipping or tax; a partial refund is taken off
        it); <strong>gross</strong> is what the customer paid, including shipping and tax, less refunds. A state is
        flagged at {Math.round(FLAG_AT * 100)}% of either threshold in either year.
      </p>
      <p style={{ fontSize: '13px', color: '#888', margin: '0 0 16px' }}>
        Last updated {formatStamp(report.generated_at)} · refunds checked {formatStamp(report.refunds_synced_at)}
        {refundNote && <span style={{ color: '#b45309' }}> · {refundNote}</span>}
      </p>

      {prevYearPartial && (
        <div style={{
          background: '#f3f4f6', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '14px'
        }}>
          <strong>{prevYear} is incomplete here.</strong> This store's first order was{' '}
          {firstOrder.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. Sales before
          that went through the old store and aren't in this table — use the Stripe export for {prevYear} totals.
        </div>
      )}

      {flagged.length > 0 && (
        <div style={{
          background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px',
          padding: '12px 16px', marginBottom: '16px', fontSize: '14px'
        }}>
          <strong>Watch:</strong> {flagged.map((r) => `${r.state} ${r.status.label.toLowerCase()}`).join(', ')}
        </div>
      )}

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              {header('state', 'State')}
              {header('prevOrders', `${prevYear} Orders`)}
              {header('prevSales', `${prevYear} Sales`)}
              {header('curOrders', `${curYear} Orders`)}
              {header('curSales', `${curYear} Sales`)}
              {header('status', 'Threshold')}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.state} style={{ background: row.status.background }}>
                <td style={{ fontWeight: row.status.bold ? '700' : '400' }}>{row.state}</td>
                <td>{row.previous.orders}</td>
                {salesCell(row.previous)}
                <td>{row.current.orders}</td>
                {salesCell(row.current)}
                <td style={{ color: row.status.color, fontWeight: row.status.bold ? '700' : '400' }}>{row.status.label}</td>
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid #ddd' }}>
              <td style={{ fontWeight: '700' }}>All</td>
              <td style={{ fontWeight: '700' }}>{report.totals.previous.orders}</td>
              {totalCell(report.totals.previous)}
              <td style={{ fontWeight: '700' }}>{report.totals.current.orders}</td>
              {totalCell(report.totals.current)}
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: '13px', color: '#888', marginTop: '12px' }}>
        Refunded in full and left out: {report.refunded_excluded.previous} in {prevYear},{' '}
        {report.refunded_excluded.current} in {curYear}. "Unknown" = orders saved without a shipping address.
        Click a column heading to sort.
      </p>

      <ThresholdSettings config={config} onSaved={setConfig} />

      <StateMonthOrders
        stateOptions={[...new Set(['NC', 'VA', ...report.states.map((s) => s.state)])].sort()}
      />
    </>
  );
}

function ThresholdSettings({ config, onSaved }) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const startEditing = () => setDraft({
    default_orders: config.default_orders ?? '',
    default_sales: config.default_sales ?? '',
    default_basis: config.default_basis,
    registered: config.registered.join(', '),
    overrides: Object.entries(config.overrides).map(([state, rule]) => ({
      state, orders: rule.orders ?? '', sales: rule.sales ?? '', basis: rule.basis || 'goods',
    })),
  });

  const number = (value) => (value === '' || value === null ? null : Number(value));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put('/admin/sales-tax/thresholds', {
        default_orders: number(draft.default_orders),
        default_sales: number(draft.default_sales),
        default_basis: draft.default_basis,
        registered: draft.registered.split(/[\s,]+/).filter(Boolean),
        overrides: Object.fromEntries(draft.overrides.filter((o) => o.state.trim()).map((o) => [
          o.state.trim(), { orders: number(o.orders), sales: number(o.sales), basis: o.basis },
        ])),
      });
      onSaved(data);
      setDraft(null);
      toast.success('Thresholds saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save thresholds');
    } finally {
      setSaving(false);
    }
  };

  const describe = (rule) => [
    rule.orders ? `${rule.orders} orders` : null,
    rule.sales ? `${dollars(rule.sales)} ${rule.basis === 'gross' ? 'gross' : 'in goods'}` : null,
  ].filter(Boolean).join(' or ') || 'no threshold';

  const boxStyle = { marginTop: '24px', padding: '16px', border: '1px solid #eee', borderRadius: '8px', background: '#fff' };
  const smallInput = { width: '110px' };

  if (!draft) {
    return (
      <div style={boxStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <strong>Thresholds</strong>
          <button className="btn btn-secondary" onClick={startEditing}>Edit thresholds</button>
        </div>
        <p style={{ fontSize: '14px', color: '#555', margin: '8px 0 0', lineHeight: 1.6 }}>
          Every state: {describe({ orders: config.default_orders, sales: config.default_sales, basis: config.default_basis })} per
          calendar year.
          {Object.entries(config.overrides).map(([state, rule]) => (
            <span key={state}><br />{state}: {describe(rule)}</span>
          ))}
          <br />Registered (shown, never flagged): {config.registered.join(', ') || 'none'}
        </p>
      </div>
    );
  }

  const setOverride = (index, field, value) => setDraft({
    ...draft,
    overrides: draft.overrides.map((o, i) => (i === index ? { ...o, [field]: value } : o)),
  });

  return (
    <div style={boxStyle}>
      <strong>Thresholds</strong>
      <p style={{ fontSize: '13px', color: '#888', margin: '4px 0 12px' }}>
        Leave a box empty if that state has no such test. "Gross" measures what customers paid including shipping
        and tax; "goods" leaves those out.
      </p>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
        <span style={{ fontSize: '14px', width: '110px' }}>Every state</span>
        <input className="form-input" style={smallInput} type="number" min="0" placeholder="orders"
          value={draft.default_orders} onChange={(e) => setDraft({ ...draft, default_orders: e.target.value })} />
        <span style={{ fontSize: '14px' }}>orders or $</span>
        <input className="form-input" style={smallInput} type="number" min="0" placeholder="sales"
          value={draft.default_sales} onChange={(e) => setDraft({ ...draft, default_sales: e.target.value })} />
        <select className="form-input" style={{ width: '110px' }} value={draft.default_basis}
          onChange={(e) => setDraft({ ...draft, default_basis: e.target.value })}>
          <option value="goods">goods</option>
          <option value="gross">gross</option>
        </select>
      </div>
      {draft.overrides.map((o, index) => (
        <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
          <input className="form-input" style={{ width: '110px' }} placeholder="State (VA)" maxLength={2}
            value={o.state} onChange={(e) => setOverride(index, 'state', e.target.value.toUpperCase())} />
          <input className="form-input" style={smallInput} type="number" min="0" placeholder="orders"
            value={o.orders} onChange={(e) => setOverride(index, 'orders', e.target.value)} />
          <span style={{ fontSize: '14px' }}>orders or $</span>
          <input className="form-input" style={smallInput} type="number" min="0" placeholder="sales"
            value={o.sales} onChange={(e) => setOverride(index, 'sales', e.target.value)} />
          <select className="form-input" style={{ width: '110px' }} value={o.basis}
            onChange={(e) => setOverride(index, 'basis', e.target.value)}>
            <option value="goods">goods</option>
            <option value="gross">gross</option>
          </select>
          <button className="btn btn-secondary" title="Remove this state's rule"
            onClick={() => setDraft({ ...draft, overrides: draft.overrides.filter((_, i) => i !== index) })}>
            Remove
          </button>
        </div>
      ))}
      <button className="btn btn-secondary" style={{ marginBottom: '12px' }}
        onClick={() => setDraft({
          ...draft,
          overrides: [...draft.overrides, {
            state: '', orders: draft.default_orders, sales: draft.default_sales, basis: draft.default_basis,
          }],
        })}>
        Add a state rule
      </button>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
        <span style={{ fontSize: '14px', width: '110px' }}>Registered</span>
        <input className="form-input" style={{ width: '240px' }} placeholder="NC, VA"
          value={draft.registered} onChange={(e) => setDraft({ ...draft, registered: e.target.value })} />
        <span style={{ fontSize: '13px', color: '#888' }}>shown but never flagged</span>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save thresholds'}</button>
        <button className="btn btn-secondary" onClick={() => setDraft(null)} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}

function AbandonedTable({ abandoned, stats, formatDate }) {
  if (abandoned.length === 0) {
    return (
      <div className="empty-state">
        <ClipboardList size={48} strokeWidth={1} />
        <h3>No abandoned checkouts in the last 90 days</h3>
      </div>
    );
  }

  return (
    <>
      <p style={{ fontSize: '14px', color: '#555', margin: '0 0 16px', lineHeight: 1.6 }}>
        Checkouts from the last 90 days where the shopper reached the Stripe payment page but never paid.
        Each one used up an order number, which is why the order sequence has gaps.{' '}
        <strong>{stats.lostCount}</strong> abandoned (${stats.lostValue.toFixed(2)} in carts, before shipping)
        {stats.retriedCount > 0 && (
          <>; <strong>{stats.retriedCount}</strong> may be the shopper coming back and paying in a later checkout (same cart, within an hour)</>
        )}.
      </p>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Order #</th>
              <th></th>
              <th>Item(s)</th>
              <th>Cart Value</th>
              <th>Started</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {abandoned.map((a) => (
              <tr key={`${a.shop}-${a.id}`}>
                <td>{a.order_number}</td>
                <td>
                  {a.image_url && (
                    <img
                      src={a.image_url}
                      alt=""
                      style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '4px', display: 'block' }}
                    />
                  )}
                </td>
                <td>
                  {a.items.map((item, i) => (
                    <div key={i} style={{ fontSize: '14px' }}>
                      {item.title}
                      {item.variant_label && <span style={{ color: '#888' }}> — {item.variant_label}</span>}
                      {item.quantity > 1 && <span style={{ color: '#888' }}> x{item.quantity}</span>}
                    </div>
                  ))}
                </td>
                <td>${a.subtotal?.toFixed(2)}</td>
                <td>
                  {formatDate(a.created_at)}
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    {new Date(a.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </div>
                </td>
                <td style={{ fontSize: '13px' }}>
                  {a.completed_as ? (
                    <span style={{ color: '#166534' }}>Possibly paid later as {a.completed_as}</span>
                  ) : a.in_progress ? (
                    <span style={{ color: '#92400e' }}>May still be checking out</span>
                  ) : (
                    <span style={{ color: '#991b1b' }}>Abandoned</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default AdminOrders;
